import { useTranslation } from 'react-i18next'
import Card from '../../../components/ui/Card'
import StatusTag from '../../../components/ui/StatusTag'
import Badge from '../../../components/ui/Badge'
import DetailList, { type DetailItem } from '../../../components/ui/DetailList'
import { TICKET_REQUEST_STATUS, type TicketRequestDetail } from '../types'
import { tryCurrency } from '../format'
import { formatDate } from '../../../lib/datetime'

interface DetailCardProps {
  detail: TicketRequestDetail
}

/**
 * Talep Detayı — sol kart (salt-okuma; 013 migrasyon: PrimeReact Card + ui/StatusTag + ui/DetailList).
 * Üstte PNR + tür çipi + durum rozeti; meta ızgarası; Gerekçe/Notlar; "Diğer" açıklaması; reddedilmişse
 * red gerekçesi/açıklaması. Onay/Red **yok** (FR-005 — onay/ret listeden yapılır).
 */
export default function DetailCard({ detail }: Readonly<DetailCardProps>) {
  const { t, i18n } = useTranslation()
  const currency = tryCurrency(i18n.language)
  const rejected = detail.status?.id === TICKET_REQUEST_STATUS.REJECTED

  const items: DetailItem[] = [
    { label: t('tickets.detail.fields.passenger'), value: detail.nameSurname },
    { label: t('tickets.detail.fields.type'), value: detail.type?.name },
    { label: t('tickets.detail.fields.office'), value: detail.office?.name },
    {
      label: t('tickets.detail.fields.price'),
      value: detail.ticketPrice == null ? null : currency.format(detail.ticketPrice),
    },
    {
      label: t('tickets.detail.fields.requester'),
      value: `${detail.requestUser?.displayName ?? '—'} · ${formatDate(detail.creDate)}`,
    },
    // Figma: "Gerekçe" da bilgi ızgarasında bir satır (ayrı blok değil).
    { label: t('tickets.detail.fields.note'), value: detail.requestNote },
  ]

  return (
    <Card className="tk-detail-card" aria-label={t('tickets.detail.infoTitle')}>
      <header className="tk-detail-top">
        {/* Figma: "PNR / Bilet No: {kod}" + tür ve durum rozetleri hep birlikte solda. */}
        <span className="tk-detail-pnrlabel">{t('tickets.detail.pnrLabel')}</span>
        <span className="tk-detail-pnr">{detail.pnr}</span>
        {detail.type?.name && <Badge tone="neutral">{detail.type.name}</Badge>}
        <StatusTag statusId={detail.status?.id} label={detail.status?.name} />
      </header>

      <DetailList items={items} />

      {detail.otherRequestNote && (
        <div className="tk-detail-block">
          <span className="tk-detail-blocklabel">{t('tickets.detail.fields.otherNote')}</span>
          <p className="tk-detail-text">{detail.otherRequestNote}</p>
        </div>
      )}

      {rejected && detail.rejectReason && (
        <div className="tk-detail-block tk-detail-reject">
          <span className="tk-detail-blocklabel">{t('tickets.detail.fields.rejectReason')}</span>
          <p className="tk-detail-text">{detail.rejectReason.name}</p>
          {detail.rejectNote && (
            <>
              <span className="tk-detail-blocklabel">{t('tickets.detail.fields.rejectNote')}</span>
              <p className="tk-detail-text">{detail.rejectNote}</p>
            </>
          )}
        </div>
      )}
    </Card>
  )
}
